import classNames from 'classnames';
import { extent, scaleBand, scaleLinear } from 'd3';
import { useRef } from 'react';

import { useResizeObserver } from '@revelio/core';

import { ReplotChartNames } from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import {
  getFormatter,
  getMaxTextWidth,
  getPrimaryColor,
} from '../../utilities';
import { plotTestId } from '../../utilities/plotTestId';
import { AxisLabel } from '../axis/axis-label';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import styles from './bar-chart.module.css';
import { BarLabel } from './bar-label';
import { BarTooltip } from './bar-tooltip';

const BAR_LEFT_PADDING = 4;

const MAX_BAR_LABEL_SIZE = 40;
const MIN_BAR_LABEL_SIZE = 30;
const BAR_LABEL_PADDING = 4;
const MIN_BAR_WIDTH = 1;

const DIMENSIONS_STANDARD = {
  fontSize: 11,
  paddingOuter: 0.4,
  paddingInner: 0.4,
};

const DIMENSIONS_SMALL = {
  fontSize: 10,
  paddingOuter: 0.4,
  paddingInner: 0.4,
};

export type BarData<TData extends object = object> = {
  label: string;
  value: number | null;
  color?: string;
  id?: string | null;
} & TData;

type AxisOptions = {
  min: number;
  max: number;
};

export type BarChartProps<TData extends object = object> = {
  className?: string;
  data: BarData<TData>[];
  showBarLabels?: boolean;
  xAxis?: AxisOptions;
  tooltipContent?: (data: BarData<TData>) => React.ReactNode;
  highlightOnHover?: boolean;
} & CommonPlotProps;

export const BarChart = <TData extends object = object>({
  className,
  data = [],
  showBarLabels = true,
  format = FormatType.SI,
  currencyCode = 'USD',
  loading = false,
  xAxis,
  tooltipContent,
  highlightOnHover = false,
}: BarChartProps<TData>) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { width, height } = useResizeObserver(containerRef);

  const isSmallPlot = width < 250;
  const dimensions = isSmallPlot ? DIMENSIONS_SMALL : DIMENSIONS_STANDARD;
  const { fontSize, paddingOuter, paddingInner } = dimensions;

  // Use a unique domain key for each bar (its index) so that duplicate labels
  // are treated as separate entries in the band scale.
  const yDomain = data.map((_, i) => i.toString());
  const yScale = scaleBand<string>(yDomain, [0, height])
    .paddingOuter(paddingOuter)
    .paddingInner(paddingInner);

  // Prepare display labels that remain unique by appending an index when the
  // same label appears multiple times (e.g., "London", "London (2)").
  const labelCounts = new Map<string, number>();
  const displayLabels = data.map((d) => {
    const count = labelCounts.get(d.label) ?? 0;
    labelCounts.set(d.label, count + 1);
    return count === 0 ? d.label : `${d.label} (${count + 1})`;
  });

  const yAxisWidth = Math.min(
    getMaxTextWidth({ texts: displayLabels }),
    width * 0.35
  );

  const allValues = data.map((d) => d.value).filter((v) => v !== null);
  const formatValue = getFormatter(format, {
    values: allValues,
    currencyCode,
  });
  const totalBarLabelWidth = getMaxTextWidth({
    texts: allValues.map((v) => formatValue(v)),
  });
  const barLabelSize =
    Math.min(
      MAX_BAR_LABEL_SIZE,
      Math.max(MIN_BAR_LABEL_SIZE, totalBarLabelWidth)
    ) + BAR_LABEL_PADDING;
  const plotWidth =
    width -
    yAxisWidth -
    BAR_LEFT_PADDING -
    (showBarLabels ? barLabelSize + BAR_LABEL_PADDING : 0);

  const isPlotSizeValid = plotWidth > 0 && height > 0;

  const everyValueNegative = allValues.every((v) => v <= 0);
  const everyValuePositive = allValues.every((v) => v >= 0);
  const [minValue, maxValue] = xAxis
    ? [xAxis.min, xAxis.max]
    : extent(allValues);

  const xScale = (() => {
    if (minValue === undefined || maxValue === undefined) return null;

    const forceZero = !xAxis;

    const zeroedMinValue =
      maxValue > 0 && forceZero ? Math.min(minValue, 0) : minValue;
    const zeroedMaxValue = maxValue < 0 && forceZero ? 0 : maxValue;

    return scaleLinear([zeroedMinValue, zeroedMaxValue], [0, plotWidth]);
  })();

  const tooltipFormatValue = getFormatter(
    format === FormatType.SI ? FormatType.INTEGER : format,
    { values: allValues, increasePrecision: 1, currencyCode }
  );

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, className)}
      data-testid={plotTestId(ReplotChartNames.BarChart)}
    >
      <PlotLoadWrapper loading={loading} noData={data.length === 0}>
        <svg width="100%" height="100%">
          <defs>
            <pattern
              id="diagonal-stripe-4"
              patternUnits="userSpaceOnUse"
              width="10"
              height="10"
            >
              <image
                xlinkHref="data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxMCcgaGVpZ2h0PScxMCc+CiAgPHJlY3Qgd2lkdGg9JzEwJyBoZWlnaHQ9JzEwJyBmaWxsPScjQ0JENUUwJy8+CiAgPHBhdGggZD0nTS0xLDEgbDIsLTIKICAgICAgICAgICBNMCwxMCBsMTAsLTEwCiAgICAgICAgICAgTTksMTEgbDIsLTInIHN0cm9rZT0nI2ZmZicgc3Ryb2tlLXdpZHRoPSczJy8+Cjwvc3ZnPg=="
                x="0"
                y="0"
                width="10"
                height="10"
              ></image>
            </pattern>
          </defs>
          <g id="y-axis" data-testid="y-axis">
            {isPlotSizeValid &&
              yDomain.map((domain) => {
                const y = yScale(domain);
                const labelIndex = parseInt(domain, 10);
                const axisLabel = displayLabels[labelIndex] ?? domain;
                return (
                  y && (
                    <AxisLabel
                      key={domain}
                      y={y + yScale.bandwidth() / 2}
                      width={yAxisWidth}
                      fontSize={fontSize}
                      label={axisLabel}
                      availableHeight={yScale.bandwidth()}
                    />
                  )
                );
              })}
          </g>
          <g
            id="chart"
            transform={`translate(${yAxisWidth + BAR_LEFT_PADDING}, 0)`}
            className={classNames({
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore noUncheckedIndexedAccess
              [styles.highlightOnHover]: highlightOnHover,
            })}
          >
            {isPlotSizeValid &&
              xScale &&
              data.map((d, i) => {
                const { label, value, color } = d;

                const baselineValue =
                  xAxis && everyValuePositive && minValue ? minValue : 0;
                const xStart = xScale(baselineValue);
                const xEnd = value === null ? xScale.range()[1] : xScale(value);

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore noUncheckedIndexedAccess
                const x = Math.min(xStart, xEnd);
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore noUncheckedIndexedAccess
                const calculatedWidth = Math.abs(xEnd - xStart);
                const barWidth =
                  calculatedWidth === 0
                    ? 0
                    : Math.max(MIN_BAR_WIDTH, calculatedWidth);

                const y = yScale(i.toString());
                const maxBarLabelWidth = everyValueNegative
                  ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore noUncheckedIndexedAccess
                    xScale.range()[1] - plotWidth + barLabelSize
                  : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore noUncheckedIndexedAccess
                    Math.max(xScale.range()[1] - barWidth + barLabelSize, 0);

                return (
                  barWidth >= 0 &&
                  maxBarLabelWidth >= 0 && (
                    <g key={`${label}_${i}`} id="bar-group">
                      <BarTooltip
                        label={
                          tooltipContent ? (
                            tooltipContent(d)
                          ) : (
                            <div>
                              <div className={styles.barTooltipLabel}>
                                {label}
                              </div>
                              <div className={styles.barTooltipValue}>
                                {value === null
                                  ? '-'
                                  : tooltipFormatValue(value)}
                              </div>
                            </div>
                          )
                        }
                      >
                        <rect
                          x={x}
                          y={y}
                          width={barWidth}
                          height={yScale.bandwidth()}
                          rx={2}
                          fill={
                            value === null
                              ? `url(#diagonal-stripe-4)`
                              : color || getPrimaryColor()
                          }
                          className={styles.bar}
                          data-testid="bar-chart__bar"
                        />
                      </BarTooltip>
                      {showBarLabels && y && (
                        <BarLabel
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore noUncheckedIndexedAccess
                          x={Math.max(xEnd, xStart) + BAR_LABEL_PADDING}
                          y={y}
                          width={maxBarLabelWidth}
                          height={yScale.bandwidth()}
                          fontSize={fontSize}
                          label={value === null ? '' : formatValue(value)}
                        />
                      )}
                    </g>
                  )
                );
              })}
          </g>
        </svg>
      </PlotLoadWrapper>
    </div>
  );
};
